import React, { type ReactNode } from "react";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";

export function StatCard({
  label,
  value,
  delta,
  hint,
  icon,
  highlight,
}: {
  label: string;
  value: string;
  delta?: number;
  hint?: string;
  icon?: ReactNode;
  highlight?: boolean;
}) {
  const positive = (delta ?? 0) >= 0;
  // Determine responsive font size based on value length
  // Monetary values like "R$ 12.345,67" are ~13 chars; needs smaller fonts in compact 6-col grid
  const len = value.length;
  const fontSizeClass =
    len > 13
      ? "text-sm xl:text-xs 2xl:text-sm"
      : len > 10
      ? "text-base xl:text-sm 2xl:text-base"
      : len > 7
      ? "text-lg xl:text-base 2xl:text-lg"
      : "text-2xl xl:text-lg 2xl:text-xl";

  return (
    <div className={`card-premium p-4 sm:p-5 xl:p-3 2xl:p-5 relative overflow-hidden ${highlight ? "border-primary/30 bg-primary/5" : ""}`}>
      <div className="flex items-start justify-between gap-1">
        <div className="label-eyebrow text-muted-foreground leading-tight" style={{ fontSize: "10px", wordBreak: "break-word" }} title={label}>{label}</div>
        {icon && (
          <div className={`h-7 w-7 xl:h-6 xl:w-6 2xl:h-8 2xl:w-8 rounded-lg flex items-center justify-center shrink-0 ${highlight ? "bg-primary/20 text-primary" : "bg-secondary text-muted-foreground"}`}>
            {icon}
          </div>
        )}
      </div>
      <div
        className={`mt-3 font-display font-bold tracking-tight leading-tight ${fontSizeClass} ${highlight ? "text-primary" : ""}`}
        title={value}
        style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
      >
        {value}
      </div>
      <div className="mt-2 flex items-center gap-2 text-xs">
        {delta !== undefined && (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md ${
              positive ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"
            }`}
          >
            {positive ? (
              <ArrowUpRight className="h-3 w-3" />
            ) : (
              <ArrowDownRight className="h-3 w-3" />
            )}
            {Math.abs(delta).toFixed(1)}%
          </span>
        )}
        {hint && <span className="text-muted-foreground truncate">{hint}</span>}
      </div>
    </div>
  );
}

export function SectionCard({
  title,
  subtitle,
  action,
  children,
  className = "",
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`card-premium min-w-0 ${className}`}>
      <header className="flex min-w-0 items-start justify-between gap-3 border-b border-border px-4 pb-3 pt-4 sm:items-center sm:px-6 sm:pb-4 sm:pt-5">
        <div className="min-w-0">
          <h2 className="font-display text-sm font-semibold sm:text-base">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </header>
      <div className="min-w-0 p-4 sm:p-6">{children}</div>
    </section>
  );
}

export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; fg: string; label: string; border?: string }> = {
    // Novos Status Padronizados
    CONFIRMADO: {
      bg: "bg-[rgba(22,163,74,0.15)]",
      fg: "text-[#22c55e]",
      label: "Confirmado",
      border: "border-[rgba(34,197,94,0.35)]",
    },
    ORCAMENTO_ENVIADO: { bg: "bg-warning/10", fg: "text-warning", label: "Orçamento Enviado" },
    DADOS_SOLICITADOS: { bg: "bg-primary/10", fg: "text-primary", label: "Dados Solicitados" },
    AGUARDANDO_RESPOSTA: {
      bg: "bg-secondary",
      fg: "text-muted-foreground",
      label: "Aguardando Resposta",
    },
    FINALIZADO: { bg: "bg-success/10", fg: "text-success", label: "Finalizado" },
    CANCELADO: { bg: "bg-destructive/10", fg: "text-destructive", label: "Cancelado" },
    NOVO: { bg: "bg-primary/10", fg: "text-primary", label: "Novo" },
    REALIZADO: {
      bg: "bg-[rgba(22,163,74,0.15)]",
      fg: "text-[#22c55e]",
      label: "Realizado",
      border: "border-[rgba(34,197,94,0.35)]",
    },
    PROPOSTA_ACEITA: {
      bg: "bg-[rgba(22,163,74,0.15)]",
      fg: "text-[#22c55e]",
      label: "Proposta Aceita",
      border: "border-[rgba(34,197,94,0.35)]",
    },
    novo_orcamento: { bg: "bg-primary/10", fg: "text-primary", label: "Novo Orçamento" },
    orcamento_enviado: { bg: "bg-warning/10", fg: "text-warning", label: "Orçamento Enviado" },
    aguardando_retorno: {
      bg: "bg-secondary",
      fg: "text-muted-foreground",
      label: "Aguardando Retorno",
    },
    em_assinatura: { bg: "bg-primary/10", fg: "text-primary", label: "Em Assinatura" },
    financeiro_pago: {
      bg: "bg-[rgba(22,163,74,0.15)]",
      fg: "text-[#22c55e]",
      label: "Pago",
      border: "border-[rgba(34,197,94,0.35)]",
    },
    financeiro_em_aberto: {
      bg: "bg-warning/10",
      fg: "text-warning",
      label: "Em aberto",
      border: "border-warning/30",
    },

    // Legado e Outros
    confirmado: {
      bg: "bg-[rgba(22,163,74,0.15)]",
      fg: "text-[#22c55e]",
      label: "Confirmado",
      border: "border-[rgba(34,197,94,0.35)]",
    },
    em_andamento: { bg: "bg-warning/10", fg: "text-warning", label: "Em andamento" },
    rascunho: { bg: "bg-muted", fg: "text-muted-foreground", label: "Rascunho" },
    concluido: { bg: "bg-secondary", fg: "text-foreground", label: "Concluído" },
    cancelado: { bg: "bg-destructive/10", fg: "text-destructive", label: "Cancelado" },
    ativo: { bg: "bg-success/10", fg: "text-success", label: "Ativo" },
    inativo: { bg: "bg-muted", fg: "text-muted-foreground", label: "Inativo" },
    assinado: { bg: "bg-success/10", fg: "text-success", label: "Assinado" },
    enviado: { bg: "bg-warning/10", fg: "text-warning", label: "Enviado" },
  };
  const s = map[status] || { bg: "bg-secondary", fg: "text-foreground", label: status };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-medium uppercase tracking-wider ${s.bg} ${s.fg} ${s.border ? `border ${s.border}` : ""}`}
    >
      {s.label}
    </span>
  );
}

export const PrimaryButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ children, ...rest }, ref) => {
  return (
    <button
      ref={ref}
      {...rest}
      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:brightness-110 transition-all shadow-[0_8px_24px_-12px_var(--primary)] ${rest.className ?? ""}`}
    >
      {children}
    </button>
  );
});
PrimaryButton.displayName = "PrimaryButton";

export const GhostButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ children, ...rest }, ref) => {
  return (
    <button
      ref={ref}
      {...rest}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-surface text-sm hover:border-border-strong transition-all ${rest.className ?? ""}`}
    >
      {children}
    </button>
  );
});
GhostButton.displayName = "GhostButton";

export function MiniBars({ data, height = 60 }: { data: number[]; height?: number }) {
  const max = Math.max(...data, 1);
  return (
    <div className="flex items-end gap-1" style={{ height }}>
      {data.map((v, i) => (
        <div
          key={i}
          className="flex-1 rounded-sm bg-gradient-to-t from-primary/70 to-primary-glow/80"
          style={{ height: `${(v / max) * 100}%`, minHeight: 3 }}
        />
      ))}
    </div>
  );
}
